import {
    ArrowTopRightIcon,
    AtIcon,
    BugIcon,
    CardAltIcon,
    CardIcon,
    CopyIcon,
    FlexFeeIcon,
    PhoneIcon,
    RefreshIcon,
    TicketIcon,
    TransferArrowIcon,
    VerifoneImage,
} from "@xola/icons";
import { flash, getSymbol, Sidebar, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { isCreditCardPaymentLineItem } from "common";
import { find, merge, noop, set, startCase, trim } from "lodash";
import React, { ReactElement, ReactNode, useState } from "react";
import { useIsFetching } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { FeatureRole, UserRole } from "types";
import UAParser from "ua-parser-js";
import { MenuItem } from "./MenuItem";
import { queryClient } from "@/api/queryClient";
import { Currency } from "@/components/Common/Currency";
import { basicInfo, copyInfo } from "@/components/Support/SystemInformation";
import { config } from "@/config";
import { leftShiftByTwo } from "@/helpers/mathHelper";
import { usePurchase } from "@/hooks/resources/usePurchase";
import { useTraveler } from "@/hooks/resources/useTraveler";
import { useSeller, useUser } from "@/hooks/useAccount";
import { XolaShortLogo } from "@/icons/XolaShortLogo";
import { isOnFlexSubscription } from "@/models/PartnerFeeFormula";
import { hasFeature } from "@/models/Seller";

const PURCHASES_RE = /\/purchases\/([\da-z]{24})\/([\da-z]{24})/i;
const CUSTOMERS_RE = /\/customers\/contacts\/([\da-z]{24})/i;

const { userAgent } = navigator;
const ua = new UAParser(userAgent);

export const AdminMenu = () => {
    const user = useUser();
    const seller = useSeller();
    const history = useHistory();
    const flags = window.flags ?? {};
    const isFetchingCount = useIsFetching();
    const hasEMV = hasFeature(seller, FeatureRole.EMV);
    const adminApp = config.admin?.url ?? window.location.origin.replace("seller", "admin");
    const adminSellerUrl = `${adminApp}/sellers/${seller.id}`;

    // Track global states internally for showing flags
    const [debugState, setDebugState] = useState(flags.debug ?? false);
    const [simulateStripeState, setSimulateStripeState] = useState(flags.stripe?.simulateTerminal ?? false);

    const location = useLocation();
    const jiraUrl = `https://xola01.atlassian.net/secure/QuickSearch.jspa?searchString=${seller.id}`;

    const handleEmailHistory = () => {
        history.push("/emails");
    };

    const handleMessageDelivery = () => {
        history.push("/messages");
    };

    const handleReImpersonate = () => {
        let pathname = trim(location.pathname, "/");
        pathname = pathname.length > 0 ? pathname : "dashboard";
        const fullPath = window.location.origin + "/" + pathname + `/#seller=${seller.id}&apiKey=${user.apiKey}`;
        const redactedUrl = fullPath.slice(0, -1 * (user.apiKey?.length ?? 0)) + "_redacted_";
        console.debug("Temporary debugging. URL for re-impersonate is", redactedUrl);

        // This weird approach is required because for some reason in India it does not work properly
        // Doing inside a setTimeout because weird things happen, and you end up on the login page
        // If this fix does not work, then best thing is to set a cookie for 5s and then re-impersonate and not put
        // the API key in the address bar.
        setTimeout(() => {
            try {
                // Note - history.push() will not work here
                window.location.assign(fullPath);
            } catch (error) {
                console.log(`Error caught on re-impersonate`, { error });
                window.location.href = fullPath;
            }

            setTimeout(() => {
                // Need a little delay for the browser to update otherwise window.reload() will not work
                const params = {
                    redactedUrl,
                    locationHref: window.location.href.slice(0, -1 * (user.apiKey?.length ?? 0)) + "_redacted_",
                };
                if (fullPath === window.location.href) {
                    console.log(`%cRe-impersonating via window.location.reload()`, "color:red", params);
                    window.location.reload();
                } else {
                    console.log(`%cNo need to reload, already re-impersonated`, "color:green", params);
                }
            }, 500);
        }, 500);
    };

    const handleToggleDebug = () => {
        if (!window.flags) {
            window.flags = {};
        }

        const debugMode = flags.debug ?? false;
        window.flags.debug = !debugMode;
        setDebugState(window.flags.debug);

        flash.dismiss();
        setTimeout(() => {
            if (flags.debug) {
                flash.success("Debug mode enabled. The browser console will now show you more info.");
            } else {
                flash.secondary("Debug mode disabled.");
            }
        }, 500);
    };

    const handleSimulateStripe = () => {
        if (!window.flags) {
            window.flags = { stripe: { simulateTerminal: false } };
        }

        const simulateMode = flags.stripe?.simulateTerminal ?? false;
        set(window.flags, "stripe.simulateTerminal", !simulateMode);
        setSimulateStripeState(!simulateMode);

        flash.dismiss();
        setTimeout(() => {
            if (flags.stripe?.simulateTerminal) {
                flash.caution("Stripe EMV Terminal Emulation enabled. Be careful.");
            } else {
                flash.success("Stripe EMV Terminal Emulation disabled. You will now need a real EMV device.");
            }
        }, 500);
    };

    const windowLocation = window.location.href.replace(/^\s+/gm, "");
    const handleCopyFullInfo = (e: any) => {
        e.stopPropagation();
        flash.dismiss();

        const message = copyInfo(seller, user, ua, true) + windowLocation + `\n${adminSellerUrl}`;
        flashMessage(message);
    };

    const handleCopyBasicInfo = (e: any) => {
        e.stopPropagation();
        flash.dismiss();

        const message = basicInfo(seller, user) + `\n${windowLocation}\n`;
        flashMessage(message);
    };

    if (!user.roles.includes(UserRole.ADMIN)) {
        // This component should only be called if the user has admin roles, but we're still keeping this here
        // as a backup
        return null;
    }

    const plan = seller.subscriptionPlan?.package?.type ?? "free";
    let planName = plan === "free" ? "Flex Standard (Default)" : startCase(plan.toLowerCase());
    if (isOnFlexSubscription(seller)) {
        planName = plan === "free" ? "Flex Standard" : startCase(plan.toLowerCase());
    }

    return (
        <MenuItem
            icon={XolaShortMenuLogo}
            label="Debug"
            content={
                <span>
                    <AdminMenuItem icon={<CopyIcon />} onClick={noop}>
                        <span className="mx-0.5 hover:border-b hover:border-white" onClick={handleCopyBasicInfo}>
                            Copy Seller Info
                        </span>
                        <span onClick={handleCopyBasicInfo}>+</span>
                        <span className="mx-0.5 hover:border-b hover:border-white" onClick={handleCopyFullInfo}>
                            Browser Info
                        </span>
                    </AdminMenuItem>

                    <AdminMenuItem icon={<BugIcon className="!h-4 !w-4" />} onClick={handleToggleDebug}>
                        {debugState ? "Disable Debug Mode" : "Enable Debug Mode"}
                    </AdminMenuItem>

                    {hasEMV && (
                        <AdminMenuItem
                            icon={<VerifoneImage className="relative right-1.5 !-mr-1 !contrast-50 !filter" />}
                            onClick={handleSimulateStripe}
                        >
                            {simulateStripeState ? "Disable EMV Simulation" : "Enable EMV Simulation"}
                        </AdminMenuItem>
                    )}

                    <Sidebar.Separator className="!my-2" />

                    <AdminMenuItem icon={<AtIcon />} onClick={handleEmailHistory}>
                        Email History
                    </AdminMenuItem>

                    <AdminMenuItem icon={<PhoneIcon />} onClick={handleMessageDelivery}>
                        Message Delivery
                    </AdminMenuItem>

                    <AdminMenuItem
                        icon={<TransferArrowIcon />}
                        tooltip="Will forcibly re-fetch all fetched data. Quicker than re-impersonate."
                        onClick={handleInvalidate}
                    >
                        Refetch App Data
                        <span className="pl-0.5 text-gray-dark">
                            {isFetchingCount > 0 ? (
                                <span className="inline-block w-6">({isFetchingCount})</span>
                            ) : (
                                <span className="invisible inline-block w-6" />
                            )}
                        </span>
                    </AdminMenuItem>

                    <AdminMenuItem
                        icon={<RefreshIcon />}
                        tooltip="Refresh the seller app with your credentials"
                        onClick={handleReImpersonate}
                    >
                        Re-Impersonate
                    </AdminMenuItem>

                    <Sidebar.Separator className="!my-2" />

                    <PurchaseMenuItem />

                    <AdminMenuItem icon={<TicketIcon />} tooltip="View seller's tickets in JIRA" url={jiraUrl}>
                        JIRA
                        <ArrowTopRightIcon size="tiny" className="-top-1 ml-1 !h-2.5 !w-2.5" />
                    </AdminMenuItem>

                    <StripeMenuItem />

                    <AdminMenuItem icon={<FlexFeeIcon />}>
                        {planName} <FlexFee />
                    </AdminMenuItem>
                </span>
            }
        />
    );
};

const handleInvalidate = async () => queryClient.invalidateQueries();

const flashMessage = (message: string) => {
    flash.dismiss();
    navigator.clipboard.writeText(message).then(() => {
        console.log("Message:\n" + message);
        flash.show({
            size: "large",
            color: "primary",
            duration: 5000,
            text: <div className="text-mono !truncate !whitespace-pre-line text-sm">{message}</div>,
        });
    });
};

const StripeMenuItem = () => {
    const seller = useSeller();
    const remoteId = seller.stripeAccount?.remoteId ?? "";

    const [, purchaseId] = PURCHASES_RE.exec(location.pathname) ?? [];
    const [, travelerId] = CUSTOMERS_RE.exec(location.pathname) ?? [];
    const { purchase } = usePurchase(purchaseId);
    const { traveler } = useTraveler(travelerId, { private: true });

    const stripePrefix = process.env.ENVIRONMENT === "production" ? "" : "test/";
    const accountUrl = `https://dashboard.stripe.com/${stripePrefix}connect/accounts/${remoteId}`;
    const paymentUrl = `https://dashboard.stripe.com/${stripePrefix}search?query=${purchase?.id}`;
    const travelerUrl = `https://dashboard.stripe.com/${stripePrefix}search?query=${traveler?.id}`;

    const travelerHasCreditCard = traveler?.creditCards ? traveler.creditCards.length > 0 : false;
    const paymentHasCreditCard = purchase?.lineItems.some((lineItem) => isCreditCardPaymentLineItem(lineItem));
    if (!remoteId || remoteId?.length < 1) {
        return null;
    }

    return (
        <>
            <Sidebar.Separator className="!my-2" />
            <AdminMenuItem icon={<CardAltIcon />} tooltip={`Account ID: ${remoteId}`} url={accountUrl}>
                Stripe Account
                <ArrowTopRightIcon size="tiny" className="-top-1 ml-1 !h-2.5 !w-2.5" />
            </AdminMenuItem>
            {paymentHasCreditCard && (
                <AdminMenuItem icon={<CardIcon />} tooltip="View CC transactions in Stripe" url={paymentUrl}>
                    Stripe Transactions
                    <ArrowTopRightIcon size="tiny" className="-top-1 ml-1 !h-2.5 !w-2.5" />
                </AdminMenuItem>
            )}
            {travelerHasCreditCard && (
                <AdminMenuItem icon={<CardIcon />} tooltip="View CC transactions in Stripe" url={travelerUrl}>
                    Traveler Transactions
                    <ArrowTopRightIcon size="tiny" className="-top-1 ml-1 !h-2.5 !w-2.5" />
                </AdminMenuItem>
            )}
        </>
    );
};

const PurchaseMenuItem = () => {
    const [, purchaseId, itemId] = PURCHASES_RE.exec(location.pathname) ?? [];
    const [, travelerId] = CUSTOMERS_RE.exec(location.pathname) ?? [];
    const { purchase } = usePurchase(purchaseId);
    const { traveler } = useTraveler(travelerId, { private: true });
    if (!purchase && !traveler?.email) {
        return null;
    }

    const item = find(purchase?.items, { id: itemId });
    const shortCode = item && "shortCode" in item ? item.shortCode : null;
    const searchString = shortCode ?? purchaseId;

    let email = purchase?.customerEmail;
    if (item && "recipientEmail" in item) {
        email = item.recipientEmail;
    } else if (traveler?.email) {
        email = traveler.email;
    }

    const phone = purchase?.phone;

    if (!searchString && !email && !phone) {
        return null;
    }

    email = email?.toLowerCase();
    const emailUrl = `/emails?search=${email}`;
    const shortCodeUrl = `/emails?search=${searchString}`;
    const shortCodeMessage = shortCode ? shortCode.toUpperCase() : purchaseId;
    const phoneUrl = `/messages?${new URLSearchParams({ search: phone ?? "" }).toString()}`;

    return (
        <>
            {searchString && (
                <AdminMenuItem
                    isRedirect
                    icon={<AtIcon />}
                    tooltip={<EmailTooltip value={shortCodeMessage} />}
                    url={shortCodeUrl}
                >
                    Emails {shortCodeMessage}
                    <ArrowTopRightIcon size="tiny" className="-top-1 ml-1 !h-2.5 !w-2.5" />
                </AdminMenuItem>
            )}
            {email && (
                <AdminMenuItem isRedirect icon={<AtIcon />} tooltip={<EmailTooltip value={email} />} url={emailUrl}>
                    Emails {email.length > 15 ? `${email.slice(0, 15)}...` : email}
                    <ArrowTopRightIcon size="tiny" className="-top-1 ml-1 !h-2.5 !w-2.5" />
                </AdminMenuItem>
            )}
            {phone && (
                <AdminMenuItem isRedirect icon={<PhoneIcon />} tooltip={<PhoneTooltip value={phone} />} url={phoneUrl}>
                    Messages {phone.length > 15 ? `${phone.slice(0, 15)}...` : phone}
                    <ArrowTopRightIcon size="tiny" className="-top-1 ml-1 !h-2.5 !w-2.5" />
                </AdminMenuItem>
            )}
        </>
    );
};

const EmailTooltip = ({ value }: { value: string }) => {
    return (
        <span>
            Search Emails for <b>{value}</b>.
        </span>
    );
};

interface PhoneTooltipProps {
    value: string;
}

const PhoneTooltip = ({ value }: PhoneTooltipProps) => {
    return (
        <span>
            Search Message Delivery for <b>{value}</b>.
        </span>
    );
};

interface AdminMenuItemProps {
    isRedirect?: boolean;
    icon: ReactElement;
    tooltip?: string | ReactElement;
    children: ReactNode;
    url?: string;
    onClick?: (property?: any) => void;
}

const AdminMenuItem = ({ isRedirect, icon, tooltip, url, onClick, children }: AdminMenuItemProps) => {
    const history = useHistory();
    const Icon = React.cloneElement(icon, {
        className: `${icon.props.className} mr-2 group-hover:text-white`,
    });
    const handleClick = (e: any) => {
        if (url) {
            if (isRedirect) {
                history.push(url);
            } else {
                window.open(url, "_blank");
            }
        } else if (onClick) {
            onClick(e);
        }
    };

    const Item = (
        <Sidebar.Link
            isSubMenuItem
            align="left"
            icon={() => Icon}
            className={clsx(
                "ui-sidebar-link",
                "group flex w-full items-center justify-start rounded px-5 py-2 leading-none text-gray",
                "hover:bg-gray-darker hover:text-white [&>svg:nth-child(3)]:hidden",
                !onClick && !url && "cursor-default",
            )}
            onClick={handleClick}
        >
            {children}
        </Sidebar.Link>
    );

    return tooltip ? (
        <Tooltip offset={[0, 15]} content={tooltip} placement="right">
            {Item}
        </Tooltip>
    ) : (
        Item
    );
};

const XolaShortMenuLogo = () => {
    return <XolaShortLogo className="h-5 w-5 grayscale-[0.8] filter xl:mr-3" />;
};

const FlexFee = () => {
    const seller = useSeller();
    if (!isOnFlexSubscription(seller)) {
        return null;
    }

    const serviceFees = seller.partnerFeeFormulas ?? [];
    if (!serviceFees || (serviceFees && serviceFees.length === 0)) {
        return <span>(no fees set)</span>;
    }

    const displayFormula: any = humanizePartnerFees(serviceFees, seller.currency);
    if (!displayFormula?.amount) {
        return <span>(No valid formula)</span>;
    }

    return (
        <span>
            {displayFormula.amount}%
            {displayFormula.max && (
                <>
                    <span className="mx-0.5">+</span>
                    <Currency locale="en-US">{displayFormula.max}</Currency>
                    &nbsp;Cap
                </>
            )}
        </span>
    );
};

const humanizePartnerFees = (formulas: any, currency = "USD") => {
    if (!formulas) {
        return {};
    }

    const rates = formulas.map((f: any) => humanizePartnerFee(f, currency)).filter(Boolean);
    return find(rates, { source: "checkout" }) || find(rates, { source: "office" });
};

export const humanizePartnerFee = (f: any, currency: string) => {
    const re = /\(%(amount|product_count|quantity)%\s\*\s(\d*\.?\d+)\)/;
    if (!f.formula) {
        return typeof f.amount !== "undefined" ? { amount: f.amount, source: f.source } : { source: f.source };
    }

    const [, type, value] = f.formula.match(re);
    if (type && value) {
        if (value <= 0.01) {
            return null;
        }

        let fee = `${leftShiftByTwo(value)}%`;
        if (type === "quantity") {
            fee = `${getSymbol(currency)}${value}/guest`;
        } else if (type === "product_count") {
            fee = `${getSymbol(currency)}${value}/product`;
        }

        if (f.type !== "traveler") {
            fee += ` (${f.type} pays)`;
        }

        return merge(f, { rate: fee, amount: Number.parseFloat(fee) });
    }

    return null;
};
